import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, AlertCircle, Building2, Globe, Phone, Factory } from 'lucide-react';
import { useAuth, API } from '../App';
import { t } from '../i18n';
import { toast } from 'sonner';
import axios from 'axios';

const Register = () => {
  const [searchParams] = useSearchParams();
  const initialAsPartner = searchParams.get('as') === 'partner';

  const [asPartner, setAsPartner] = useState(initialAsPartner);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Partner-only fields
  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [partnerSpecialty, setPartnerSpecialty] = useState('stickers');
  const [createdPartner, setCreatedPartner] = useState(null);
  const [copied, setCopied] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { register, googleLogin, user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (user && !createdPartner) {
      const target = user.role === 'partner' ? '/partners/dashboard' : from;
      navigate(target, { replace: true });
    }
  }, [user, createdPartner, from, navigate]);

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Une erreur s'est produite. Veuillez reessayer.";
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caracteres');
      return;
    }
    setLoading(true);

    try {
      if (asPartner) {
        const { data } = await axios.post(`${API}/partners/register`, {
          company_name: companyName,
          contact_name: name,
          email,
          password,
          website: website || null,
          phone: phone || null,
          use_case: partnerSpecialty,
        });
        // refresh auth (partner is now logged in via cookie)
        await checkAuth();
        setCreatedPartner(data); // reveal api key once
        toast.success('Compte partenaire cree !');
      } else {
        await register(email, password, name);
        toast.success(t('auth.registerBtn') + ' !');
        navigate(from, { replace: true });
      }
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Once partner created, show api key panel
  if (createdPartner) {
    const copyKey = async () => {
      await navigator.clipboard.writeText(createdPartner.api_key);
      setCopied(true);
      toast.success('Cle API copiee');
      setTimeout(() => setCopied(false), 1500);
    };
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-2xl">
          <div className="card-brutal bg-white" data-testid="partner-key-success">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary border-2 border-charcoal flex items-center justify-center shadow-brutal">
                <Building2 className="w-6 h-6 text-charcoal" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-2xl text-charcoal">Bienvenue, {createdPartner.company_name}</h1>
                <p className="text-charcoal/60 text-sm">Votre organisation partenaire est active.</p>
              </div>
            </div>
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 mb-6 flex gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-900">
                <strong>Conservez votre cle API.</strong> Elle ne sera plus affichee en entier. Regenerez-la depuis votre tableau de bord en cas de perte.
              </div>
            </div>
            <label className="text-xs font-bold tracking-widest uppercase text-charcoal/60 mb-2 block">Votre cle API</label>
            <div className="flex gap-2">
              <code className="flex-1 bg-charcoal text-primary font-mono text-sm p-4 rounded-lg break-all border-2 border-charcoal" data-testid="partner-api-key-value">
                {createdPartner.api_key}
              </code>
              <button onClick={copyKey} data-testid="partner-copy-key-btn" className="btn-outline px-4">
                {copied ? 'Copie' : 'Copier'}
              </button>
            </div>
            <div className="mt-8 flex justify-end">
              <button
                onClick={() => navigate('/partners/dashboard')}
                className="btn-primary"
                data-testid="partner-go-dashboard"
              >
                Acceder au tableau de bord
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <div className="bg-primary w-12 h-12 rounded-full border-2 border-charcoal flex items-center justify-center shadow-brutal">
            <span className="font-heading font-bold text-xl text-charcoal">T</span>
          </div>
          <span className="font-heading font-bold text-2xl text-charcoal">Tagora</span>
        </Link>

        <div className="card-brutal">
          {/* Account type toggle */}
          <div className="grid grid-cols-2 gap-2 bg-background border-2 border-charcoal/10 rounded-xl p-1 mb-6">
            <button
              type="button"
              onClick={() => setAsPartner(false)}
              data-testid="register-mode-user"
              className={`py-2 rounded-lg text-sm font-bold transition-colors ${!asPartner ? 'bg-primary text-charcoal shadow-brutal-sm' : 'text-charcoal/60'}`}
            >
              <User className="w-4 h-4 inline mr-1" /> Particulier
            </button>
            <button
              type="button"
              onClick={() => setAsPartner(true)}
              data-testid="register-mode-partner"
              className={`py-2 rounded-lg text-sm font-bold transition-colors ${asPartner ? 'bg-primary text-charcoal shadow-brutal-sm' : 'text-charcoal/60'}`}
            >
              <Factory className="w-4 h-4 inline mr-1" /> Fabricant / Partenaire
            </button>
          </div>

          <h1 className="text-2xl font-bold text-charcoal font-heading mb-2">
            {asPartner ? 'Devenir partenaire fabricant' : t('auth.registerTitle')}
          </h1>
          <p className="text-charcoal/70 mb-6 text-sm">
            {asPartner
              ? 'Inscription gratuite, cle API instantanee. Generez des Tagora ID pour vos produits.'
              : t('auth.registerSubtitle')}
          </p>

          {!asPartner && (
            <>
              <button
                type="button"
                onClick={() => googleLogin(from)}
                data-testid="google-register-btn"
                className="w-full btn-outline flex items-center justify-center gap-3 mb-4"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.orGoogle')}
              </button>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t-2 border-charcoal/20" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-4 text-sm text-charcoal/50">{t('auth.or')}</span></div>
              </div>
            </>
          )}

          {error && (
            <div className="bg-danger/10 border-2 border-danger rounded-lg p-3 mb-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {asPartner && (
              <Field icon={Building2} label="Nom de l'entreprise" required value={companyName} onChange={setCompanyName} testId="register-company" />
            )}
            <Field icon={User} label={asPartner ? 'Nom du contact' : t('auth.name')} required value={name} onChange={setName} testId="register-name" />
            <Field icon={Mail} type="email" label={t('auth.email')} required value={email} onChange={setEmail} testId="register-email" />
            <PasswordField value={password} onChange={setPassword} show={showPassword} setShow={setShowPassword} />

            {asPartner && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field icon={Globe} label="Site web (opt.)" value={website} onChange={setWebsite} placeholder="https://" testId="register-website" />
                  <Field icon={Phone} label="Telephone (opt.)" value={phone} onChange={setPhone} testId="register-phone" />
                </div>
                <div>
                  <label className="text-xs font-bold tracking-widest uppercase text-charcoal mb-2 block">Type d'integration</label>
                  <select
                    value={partnerSpecialty}
                    onChange={(e) => setPartnerSpecialty(e.target.value)}
                    className="input-brutal w-full"
                    data-testid="register-specialty"
                  >
                    <option value="stickers">Stickers / Autocollants</option>
                    <option value="luggage-tags">Etiquettes bagages</option>
                    <option value="pet-tags">Medailles pour animaux</option>
                    <option value="keychains">Porte-cles</option>
                    <option value="apparel">Textile / Vetements</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '...' : (asPartner ? 'Creer le compte partenaire' : t('auth.registerBtn'))}
            </button>
          </form>

          <p className="text-center mt-6 text-charcoal/70 text-sm">
            {t('auth.hasAccount')}{' '}
            <Link to="/login" className="text-primary font-bold hover:underline" data-testid="login-link">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, label, type = 'text', required, value, onChange, placeholder, testId }) => (
  <div>
    <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">{label}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />}
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className={`input-brutal w-full ${Icon ? 'pl-12' : ''}`}
      />
    </div>
  </div>
);

const PasswordField = ({ value, onChange, show, setShow }) => (
  <div>
    <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">Mot de passe</label>
    <div className="relative">
      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="register-password"
        className="input-brutal w-full pl-12 pr-12"
        placeholder="Min. 8 caracteres"
        required
        minLength={8}
      />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-charcoal/40 hover:text-charcoal">
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  </div>
);

export default Register;
